import { Injectable } from '@angular/core';
import { AngularFirestore, DocumentReference } from '@angular/fire/compat/firestore';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { serverTimestamp, Timestamp } from 'firebase/firestore';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../environments/environment';

export interface VisitorChatMessage {
  id?: string;
  text: string;
  senderId: 'visitor' | 'admin' | 'system';
  senderName: string;
  timestamp: any;
  status: 'sent' | 'delivered' | 'read';
}

export interface VisitorChat {
  id?: string;
  visitorName: string;
  visitorEmail: string;
  createdAt: any;
  status: 'active' | 'resolved';
  lastMessage?: string;
  lastMessageTime?: any;
  adminEmail?: string;
}

@Injectable({
  providedIn: 'root'
})
export class VisitorChatService {
  private chatOpen = new BehaviorSubject<boolean>(false);
  chatOpen$ = this.chatOpen.asObservable();

  constructor(
    private firestore: AngularFirestore,
    private snackBar: MatSnackBar
  ) {}

  toggleChat() {
    this.chatOpen.next(!this.chatOpen.value);
  }

  async initializeChat(visitorName: string, visitorEmail: string): Promise<string> {
    try {
      console.log('Initializing chat with data:', { visitorName, visitorEmail });
      
      // Rate limiting check - 1 chat per email per hour
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      
      const existingChats = await this.firestore
        .collection<VisitorChat>('visitor_chats', ref => 
          ref.where('visitorEmail', '==', visitorEmail)
             .where('createdAt', '>', oneHourAgo)
             .where('status', '==', 'active')
             .orderBy('createdAt', 'asc')
        )
        .get()
        .toPromise();

      console.log('Existing chats query result:', {
        exists: existingChats?.empty === false,
        count: existingChats?.size
      });

      if (existingChats?.docs.length && existingChats.docs[0]) {
        const existingChatId = existingChats.docs[0].id;
        console.log('Found existing chat:', existingChatId);
        return existingChatId;
      }

      const chatRef = this.firestore.collection('visitor_chats').doc();
      const newChatId = chatRef.ref.id;

      const newChat: VisitorChat = {
        visitorName,
        visitorEmail,
        createdAt: serverTimestamp(),
        status: 'active',
        adminEmail: environment.visitorChatEmail
      };

      console.log('Creating new chat with data:', {
        chatId: newChatId,
        ...newChat
      });

      await chatRef.set(newChat);
      console.log('Chat document created successfully');

      // Send notification to admin through Firestore trigger
      const notificationData = {
        type: 'new_visitor_chat',
        chatId: newChatId,
        visitorName,
        visitorEmail,
        timestamp: serverTimestamp(),
        status: 'pending',
        adminEmail: environment.visitorChatEmail
      };

      console.log('Creating notification with data:', notificationData);
      await this.firestore.collection('notifications').add(notificationData);
      console.log('Notification created successfully');

      return newChatId;
    } catch (error) {
      console.error('Error in initializeChat:', error);
      throw error;
    }
  }

  async sendMessage(chatId: string, text: string, senderId: 'visitor' | 'admin' | 'system' = 'visitor'): Promise<void> {
    const message: VisitorChatMessage = {
      text,
      senderId,
      senderName: senderId === 'visitor' ? 'Besucher' : senderId === 'admin' ? 'Admin' : 'System',
      timestamp: serverTimestamp(),
      status: 'sent'
    };

    await this.firestore
      .collection(`visitor_chats/${chatId}/messages`)
      .add(message);

    // Update last message in chat document
    await this.firestore.doc(`visitor_chats/${chatId}`).update({
      lastMessage: text,
      lastMessageTime: serverTimestamp()
    });

    // Show snackbar notification for first message only
    const messages = await this.firestore
      .collection(`visitor_chats/${chatId}/messages`)
      .get()
      .toPromise();

    if (messages?.size === 1 && senderId === 'visitor') {
      this.snackBar.open(
        'Ihre Nachricht wurde erfolgreich gesendet. Unser Support-Team wurde benachrichtigt und wird sich in Kürze bei Ihnen melden.',
        'Schließen',
        { duration: 6000 }
      );
    }
  }

  getMessages(chatId: string): Observable<VisitorChatMessage[]> {
    return this.firestore
      .collection<VisitorChatMessage>(`visitor_chats/${chatId}/messages`, ref =>
        ref.orderBy('timestamp', 'asc')
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map(messages => messages.map(message => ({
          ...message,
          timestamp: message.timestamp?.toDate?.() || message.timestamp
        })))
      );
  }

  getChatStatus(chatId: string): Observable<'active' | 'resolved'> {
    return this.firestore
      .doc<VisitorChat>(`visitor_chats/${chatId}`)
      .valueChanges()
      .pipe(
        map(chat => chat?.status || 'active')
      );
  }

  async closeChat(chatId: string): Promise<void> {
    await this.firestore.doc(`visitor_chats/${chatId}`).update({
      status: 'resolved'
    });
    // Don't close the chat window when resolved
    // this.chatOpen.next(false);
  }
} 